import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet } from "lucide-react";
import SEO from "@/components/SEO";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login?reset=1");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-brand-bg font-body">
      <SEO
        title="Reset your password"
        description="Set a new password for your Tracker Daily Money account."
        path="/reset-password"
        noindex
      />
      <div className="max-w-md w-full mx-auto p-8">
        <Link to="/login" className="flex items-center gap-2 w-fit mb-10" data-testid="brand-logo">
          <div className="h-9 w-9 rounded-md bg-brand-primary flex items-center justify-center">
            <Wallet className="h-4 w-4 text-brand-bg" strokeWidth={1.75} />
          </div>
          <span className="font-display font-black text-xl tracking-tight text-brand-charcoal">Tracker Daily</span>
        </Link>

        <div className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-muted">Set new password</div>
        <h1 className="font-display font-black text-4xl tracking-tight text-brand-charcoal mb-8">Reset your password</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password" className="text-brand-charcoal">New password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password" className="mt-1 border-brand-border bg-brand-surface" />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-brand-charcoal">Confirm password</Label>
            <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm" className="mt-1 border-brand-border bg-brand-surface" />
          </div>
          {error && <div data-testid="reset-error" className="text-sm text-brand-danger bg-brand-danger/10 border border-brand-danger/20 rounded-md px-3 py-2">{error}</div>}
          <Button type="submit" disabled={loading || !token} data-testid="reset-submit"
            className="w-full bg-brand-primary text-brand-bg hover:bg-brand-primaryHover rounded-md py-6 text-base font-semibold">
            {loading ? "Saving…" : "Update password"}
          </Button>
          {!token && <div className="text-xs text-brand-danger">Missing token in URL. Use the link from the console.</div>}
        </form>
      </div>
    </div>
  );
}
